import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/update-profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  refreshToken: () => api.post('/auth/refresh'),
  // Ansichtsmodus: als Admin mit den Augen eines anderen Users schauen
  impersonate: (userId) => api.post('/auth/impersonate', { user_id: userId }),
  stopImpersonation: () => api.post('/auth/impersonate/stop'),
};

// Users
export const usersAPI = {
  getAll: (params = {}) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resendInvitation: (id) => api.post(`/users/${id}/resend-invitation`),
};

// Projects
export const projectsAPI = {
  getAll: (includeInactive = false) => api.get('/projects', { params: { include_inactive: includeInactive } }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Tasks
export const tasksAPI = {
  getAll: (params = {}) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  reactivate: (id) => api.post(`/tasks/${id}/reactivate`),
  // PM-specific endpoints
  getBoardView: (projectId, params = {}) => api.get(`/tasks/board/${projectId}`, { params }),
  getMyWork: (showAll = false) => api.get('/tasks/my-work', { params: { all: showAll } }),
  getInbox: () => api.get('/tasks/inbox'),
  quickInbox: (name) => api.post('/tasks/quick-inbox', { name }),
  assign: (id, assigneeId) => api.post(`/tasks/${id}/assign`, { assignee_id: assigneeId }),
  move: (id, statusId, position) => api.post(`/tasks/${id}/move`, { status_id: statusId, position }),
  getSubtasks: (id) => api.get(`/tasks/${id}/subtasks`),
  getCategories: () => api.get('/tasks/categories'),
};

// Ticket-Volltextsuche (Board-Filter + globale Recherche)
export const searchAPI = {
  tasks: (params = {}) => api.get('/search/tasks', { params }),
  commentAuthors: () => api.get('/search/comment-authors'),
};

// Time Entries
export const timeEntriesAPI = {
  getAll: (params) => api.get('/timeentries', { params }),
  getById: (id) => api.get(`/timeentries/${id}`),
  create: (data) => api.post('/timeentries', data),
  update: (id, data) => api.put(`/timeentries/${id}`, data),
  delete: (id) => api.delete(`/timeentries/${id}`),
};

// Reports
export const reportsAPI = {
  getData: (params) => api.get('/reports', { params }),
  downloadPDF: (params) => {
    return api.get('/reports/pdf', {
      params,
      responseType: 'blob',
    });
  },
  getSaved: () => api.get('/reports/saved'),
  saveReport: (data) => api.post('/reports/saved', data),
  deleteSaved: (id) => api.delete(`/reports/saved/${id}`),
};

// Sprints
export const sprintsAPI = {
  getAll: (projectId, status) => api.get('/sprints', { params: { project_id: projectId, status } }),
  getById: (id) => api.get(`/sprints/${id}`),
  create: (data) => api.post('/sprints', data),
  update: (id, data) => api.put(`/sprints/${id}`, data),
  delete: (id) => api.delete(`/sprints/${id}`),
  getTasks: (id) => api.get(`/sprints/${id}/tasks`),
  complete: (id) => api.post(`/sprints/${id}/complete`),
  getActive: (projectId) => api.get(`/sprints/project/${projectId}/active`),
};

// Task Statuses
export const taskStatusesAPI = {
  getAll: (projectId) => api.get('/task-statuses', { params: { project_id: projectId } }),
  getById: (id) => api.get(`/task-statuses/${id}`),
  create: (data) => api.post('/task-statuses', data),
  update: (id, data) => api.put(`/task-statuses/${id}`, data),
  delete: (id) => api.delete(`/task-statuses/${id}`),
  reorder: (statuses) => api.post('/task-statuses/reorder', { statuses }),
  getTasks: (id) => api.get(`/task-statuses/${id}/tasks`),
};

// Comments
export const commentsAPI = {
  getAll: (taskId) => api.get('/comments', { params: { task_id: taskId } }),
  getById: (id) => api.get(`/comments/${id}`),
  create: (data) => api.post('/comments', data),
  update: (id, data) => api.put(`/comments/${id}`, data),
  // Kommentar umhängen: intern <-> kundensichtbar (Autor oder Admin)
  setVisibility: (id, isInternal) => api.patch(`/comments/${id}/visibility`, { is_internal: isInternal }),
  delete: (id) => api.delete(`/comments/${id}`),
  getReplies: (id) => api.get(`/comments/${id}/replies`),
};

// Attachments
export const attachmentsAPI = {
  getAll: (taskId) => api.get('/attachments', { params: { task_id: taskId } }),
  getById: (id) => api.get(`/attachments/${id}`),
  upload: (taskId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('task_id', taskId);
    return api.post('/attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (id) => api.get(`/attachments/${id}/download`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/attachments/${id}`),
};

// Activities
export const activitiesAPI = {
  getAll: (params) => api.get('/activities', { params }),
  getById: (id) => api.get(`/activities/${id}`),
  getTimeline: (taskId) => api.get(`/activities/task/${taskId}/timeline`),
  getRecent: (limit = 20) => api.get('/activities/recent', { params: { limit } }),
  getMyActivity: (limit = 50) => api.get('/activities/user/my-activity', { params: { limit } }),
};

// Tags
export const tagsAPI = {
  getAll: (projectId) => api.get('/tags', { params: { project_id: projectId } }),
  getById: (id) => api.get(`/tags/${id}`),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  getTasks: (id) => api.get(`/tags/${id}/tasks`),
  getTaskTags: (taskId) => api.get(`/tags/task/${taskId}/tags`),
  addToTask: (taskId, tagId) => api.post(`/tags/task/${taskId}/tags/${tagId}`),
  removeFromTask: (taskId, tagId) => api.delete(`/tags/task/${taskId}/tags/${tagId}`),
};

// Customers
export const customersAPI = {
  getAll: (includeInactive = false) => api.get('/customers', { params: { include_inactive: includeInactive } }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  search: (query) => api.get('/customers/search', { params: { q: query } }),
};

// Project Pages (per-project mini-CMS / docs)
export const projectPagesAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/pages`),
  create: (projectId, data) => api.post(`/projects/${projectId}/pages`, data),
  get: (pageId) => api.get(`/project-pages/${pageId}`),
  update: (pageId, data) => api.put(`/project-pages/${pageId}`, data),
  delete: (pageId) => api.delete(`/project-pages/${pageId}`),
  listAttachments: (pageId) => api.get(`/project-pages/${pageId}/attachments`),
  uploadAttachment: (pageId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/project-pages/${pageId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadAttachment: (attId) => api.get(`/project-page-attachments/${attId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attId) => api.delete(`/project-page-attachments/${attId}`),
};

// Bausteinbibliothek für Longform-Angebote
export const quoteBlocksAPI = {
  getAll: (params) => api.get('/quote-blocks', { params }),
  getCategories: () => api.get('/quote-blocks/categories'),
  create: (data) => api.post('/quote-blocks', data),
  update: (id, data) => api.put(`/quote-blocks/${id}`, data),
  delete: (id) => api.delete(`/quote-blocks/${id}`),
};

// Dashboard (Admin-Kennzahlen)
export const dashboardAPI = {
  billing: () => api.get('/dashboard/billing'),
};

// Invoices
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  createFromTimeEntries: (data) => api.post('/invoices/from-timeentries', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  duplicate: (id) => api.post(`/invoices/${id}/duplicate`),
  convertToInvoice: (id) => api.post(`/invoices/${id}/convert-to-invoice`),
  delete: (id) => api.delete(`/invoices/${id}`),
  finalize: (id) => api.post(`/invoices/${id}/finalize`),
  markPaid: (id, data) => api.post(`/invoices/${id}/mark-paid`, data),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  downloadZip: (ids) => api.post('/invoices/download-zip', { invoice_ids: ids }, { responseType: 'blob' }),
  sendEmail: (id, customData = null) => api.post(`/invoices/${id}/send-email`, customData),
  getEmailDraft: (id) => api.get(`/invoices/${id}/email-draft`),

  // Invoice Items
  addItem: (invoiceId, data) => api.post(`/invoices/${invoiceId}/items`, data),
  updateItem: (invoiceId, itemId, data) => api.put(`/invoices/${invoiceId}/items/${itemId}`, data),
  deleteItem: (invoiceId, itemId) => api.delete(`/invoices/${invoiceId}/items/${itemId}`),
  reorderItems: (invoiceId, positions) => api.post(`/invoices/${invoiceId}/items/reorder`, { positions }),

  // Longform-Angebote
  addItemsBulk: (invoiceId, payload) => api.post(`/invoices/${invoiceId}/items/bulk`, payload),
  saveItemAsTemplate: (invoiceId, itemId, data) =>
    api.post(`/invoices/${invoiceId}/items/${itemId}/save-as-template`, data),
  previewPDF: (id) => api.get(`/invoices/${id}/preview-pdf`, { responseType: 'blob' }),

  // Zeitnachweis an einer bestehenden Rechnung
  getTimesheetInfo: (id) => api.get(`/invoices/${id}/timesheet`),
  previewTimesheet: (id, selection) => api.post(`/invoices/${id}/timesheet/preview`, selection),
  attachTimesheet: (id, selection) => api.post(`/invoices/${id}/timesheet`, selection),
  removeTimesheet: (id) => api.delete(`/invoices/${id}/timesheet`),
  timesheetPDF: (id) => api.get(`/invoices/${id}/timesheet-pdf`, { params: { inline: 1 }, responseType: 'blob' }),
};

export default api;
